// Stroop Victoria — score calculation
// Source: Normes_Stroop_Victoria.xls (régressions Bayard et al.)
//
// Inputs:
//   age  : patient age in years
//   nsc  : 1 if niveau I, 2 if niveau II ou III
//   times (seconds, raw):
//     tCouleurs, tMots, tInterference
//   errors (counts):
//     errCouleurs, errMots                            (Planches 1 & 2)
//     errInterfTotal, errInterfNonCorr, errInterfCorr (Planche 3)
//
// For times, expected = a + b·age + c·NSC, observed = log10(time),
// z = (expected − observed) / sd, centile = Φ(z)·100.
// Faster than expected → high centile.
//
// For Planche 3 errors, the centile is looked up in the table:
//   Centile           1   5   10  25  50  ≥75
//   Erreurs totales   10  5   4   2   1   0
//   Non-corrigées     4   3   2   1   0   0
//   Corrigées         7   4   3   1   0   0
// For Planches 1 & 2, ≥ 1 erreur est considérée comme statistiquement anormale.

// Regression coefficients: expected_log10 = intercept + ageCoef·age + nscCoef·nsc, sd
const REGRESSIONS = {
  couleurs:     { intercept: 0.80, ageCoef: 0.006, nscCoef: -0.038, sd: 0.105 },
  mots:         { intercept: 0.94, ageCoef: 0.006, nscCoef: -0.073, sd: 0.09  },
  interference: { intercept: 1.15, ageCoef: 0.008, nscCoef: -0.107, sd: 0.12  },
  ifMC:         { intercept: 0.19, ageCoef: 0.000, nscCoef: -0.038, sd: 0.09  },
  ifIC:         { intercept: 0.35, ageCoef: 0.002, nscCoef: -0.069, sd: 0.11  },
};

// Planche 3 error → centile lookup. Each row: descending error cutoffs aligned with centiles.
const P3_ERROR_CENTILES = [1, 5, 10, 25, 50, 75]; // ≥75 reported as 75
const P3_ERROR_TABLE = {
  total:    [10, 5, 4, 2, 1, 0],
  nonCorr:  [ 4, 3, 2, 1, 0, 0],
  corr:     [ 7, 4, 3, 1, 0, 0],
};

// Standard normal CDF (Abramowitz & Stegun 7.1.26)
function normcdf(z) {
  if (z < -6) return 0;
  if (z > 6) return 1;
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741,
        a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + p * x);
  const erf = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1 + sign * erf);
}

const round = (v, n = 2) => {
  if (v == null || !Number.isFinite(v)) return null;
  const f = 10 ** n;
  return Math.round(v * f) / f;
};

function computeIndex(observedTime, age, nsc, reg) {
  const t = parseFloat(observedTime);
  if (!Number.isFinite(t) || t <= 0) return null;
  const observedLog = Math.log10(t);
  const expectedLog = reg.intercept + reg.ageCoef * age + reg.nscCoef * nsc;
  const z = (expectedLog - observedLog) / reg.sd;
  const centile = normcdf(z) * 100;
  return {
    raw: t,
    log10: round(observedLog, 4),
    expectedLog10: round(expectedLog, 4),
    sd: reg.sd,
    z: round(z, 2),
    centile: round(centile, 2),
  };
}

// Lookup centile for Planche 3 error count.
// First cutoff (descending) where errors >= cutoff wins.
function lookupP3ErrorCentile(errors, cutoffs) {
  const n = parseInt(errors, 10);
  if (!Number.isFinite(n) || n < 0) return null;
  for (let i = 0; i < cutoffs.length; i++) {
    if (n >= cutoffs[i]) {
      // isMin (= "≥") when this is the last bin OR the next bin has the same cutoff,
      // because the count then equally qualifies for a higher centile (e.g. NonCorr/Corr at 0).
      const isLast = i === cutoffs.length - 1;
      const nextSame = !isLast && cutoffs[i + 1] === cutoffs[i];
      return { errors: n, centile: P3_ERROR_CENTILES[i], isMin: isLast || nextSame };
    }
  }
  return { errors: n, centile: P3_ERROR_CENTILES[P3_ERROR_CENTILES.length - 1], isMin: true };
}

function flagP12Errors(errors) {
  const n = parseInt(errors, 10);
  if (!Number.isFinite(n) || n < 0) return null;
  return { errors: n, abnormal: n >= 1 };
}

/**
 * Compute Stroop Victoria scores from raw patient inputs.
 *
 * @param {Object} input
 * @param {number} input.age   - patient age in years
 * @param {number} input.nsc   - 1 (niveau I) or 2 (niveau II/III)
 * @param {number} [input.tCouleurs]
 * @param {number} [input.tMots]
 * @param {number} [input.tInterference]
 * @param {number} [input.errCouleurs]
 * @param {number} [input.errMots]
 * @param {number} [input.errInterfTotal]
 * @param {number} [input.errInterfNonCorr]
 * @param {number} [input.errInterfCorr]
 * @returns {Object} times, interferences, errors blocks (only filled fields are returned)
 */
export function calcStroopVictoria({
  age,
  nsc,
  tCouleurs, tMots, tInterference,
  errCouleurs, errMots,
  errInterfTotal, errInterfNonCorr, errInterfCorr,
}) {
  if (!Number.isFinite(age))   return { error: "Âge requis" };
  if (nsc !== 1 && nsc !== 2)  return { error: "NSC doit être 1 (niveau I) ou 2 (niveau II/III)" };

  const out = { age, nsc, times: {}, interferences: {}, errors: {} };

  const tC = parseFloat(tCouleurs);
  const tM = parseFloat(tMots);
  const tI = parseFloat(tInterference);

  if (Number.isFinite(tC)) out.times.couleurs     = computeIndex(tC, age, nsc, REGRESSIONS.couleurs);
  if (Number.isFinite(tM)) out.times.mots         = computeIndex(tM, age, nsc, REGRESSIONS.mots);
  if (Number.isFinite(tI)) out.times.interference = computeIndex(tI, age, nsc, REGRESSIONS.interference);

  if (Number.isFinite(tC) && Number.isFinite(tM) && tC > 0) {
    out.interferences.ifMC = computeIndex(tM / tC, age, nsc, REGRESSIONS.ifMC);
  }
  if (Number.isFinite(tC) && Number.isFinite(tI) && tC > 0) {
    out.interferences.ifIC = computeIndex(tI / tC, age, nsc, REGRESSIONS.ifIC);
  }

  if (errCouleurs != null && errCouleurs !== "") out.errors.couleurs = flagP12Errors(errCouleurs);
  if (errMots     != null && errMots     !== "") out.errors.mots     = flagP12Errors(errMots);

  if (errInterfTotal   != null && errInterfTotal   !== "") out.errors.interferenceTotal   = lookupP3ErrorCentile(errInterfTotal,   P3_ERROR_TABLE.total);
  if (errInterfNonCorr != null && errInterfNonCorr !== "") out.errors.interferenceNonCorr = lookupP3ErrorCentile(errInterfNonCorr, P3_ERROR_TABLE.nonCorr);
  if (errInterfCorr    != null && errInterfCorr    !== "") out.errors.interferenceCorr    = lookupP3ErrorCentile(errInterfCorr,    P3_ERROR_TABLE.corr);

  return out;
}

export { REGRESSIONS, P3_ERROR_TABLE, P3_ERROR_CENTILES, normcdf };
